// src/pages/applications/Appointments.jsx
import { useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../../components/LocalizedLink";

/* ---------- Lightbox & helpers ---------- */
function Lightbox({ open, src, alt, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] grid place-items-center bg-black/80 p-6 backdrop-saturate-125 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={onClose}
        className="absolute right-4 top-3 text-3xl text-slate-200"
        aria-label="Close"
      >
        ×
      </button>
      <div className="grid justify-items-center">
        <img
          src={src}
          alt={alt || ""}
          className="max-w-[92vw] max-h-[86vh] rounded-xl shadow-[0_20px_60px_rgba(0,0,0,.35)] bg-[#111] object-contain"
        />
        {alt ? (
          <div className="mt-3 text-[0.95rem] text-slate-200/90 text-center">
            {alt}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ZoomImg({ src, alt, onOpen, className = "" }) {
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`rounded-xl shadow-[0_8px_28px_rgba(2,6,23,0.08)] cursor-zoom-in ${className}`}
      onClick={() => onOpen(src, alt)}
    />
  );
}

const Section = ({ children, center }) => (
  <section
    className={`border-b border-slate-200 bg-transparent px-4 py-[clamp(2.2rem,5.5vw,4.2rem)] ${
      center ? "text-center" : ""
    }`}
  >
    <div className="mx-auto w-full max-w-[min(1100px,92vw)]">{children}</div>
  </section>
);

const Grid = ({ children }) => (
  <div className="grid items-center gap-[clamp(0.8rem,2.6vw,1.4rem)] md:grid-cols-[1.25fr_1fr]">
    {children}
  </div>
);

const ImageRow = ({ children }) => (
  <div className="mt-6 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
    {children}
  </div>
);

const MutedBand = ({ children }) => (
  <h3 className="mx-auto mb-5 mt-2 inline-block border-y-2 border-slate-200 px-1.5 py-1 text-center text-[clamp(1rem,3.4vw,1.2rem)] font-extrabold uppercase tracking-wide text-slate-600">
    {children}
  </h3>
);

const KPI = ({ value, label }) => (
  <div className="min-w-[120px] rounded-xl border border-slate-900/10 bg-white px-2.5 py-2 text-center">
    <h3 className="m-0 text-[1.05rem] font-extrabold">{value}</h3>
    <p className="m-0 text-sm text-slate-500">{label}</p>
  </div>
);

/* ---------- Page ---------- */
export default function Appointments() {
  const [lb, setLb] = useState({ open: false, src: "", alt: "" });
  const openLB = (src, alt) => setLb({ open: true, src, alt });
  const closeLB = () => setLb({ open: false, src: "", alt: "" });

  const { t } = useTranslation();
  const d = (k, opts) =>
    t(`applications.detail.appointments.${k}`, opts);

  const bullets = (k) =>
    t(`applications.detail.appointments.${k}`, {
      returnObjects: true,
    });

  const kpis = t("applications.detail.appointments.solution.kpis", {
    returnObjects: true,
  });

  return (
    <Fragment>
      {/* CTA header */}
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-slate-800">
            <div className="text-lg font-semibold">
              {d("cta_top.title")}
            </div>
            <div className="text-sm text-slate-600">
              {d("cta_top.subtitle")}
            </div>
          </div>
          <LocalizedLink
            to="/contact"
            className="rounded-xl bg-indigo-600 px-4 py-2 text-white text-sm font-medium hover:bg-indigo-700"
          >
            {t("applications.cta.contact")}
          </LocalizedLink>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox open={lb.open} src={lb.src} alt={lb.alt} onClose={closeLB} />

      {/* HERO */}
      <Section center>
        <h1 className="mb-2 bg-gradient-to-r from-sky-500 to-sky-300 bg-clip-text text-center text-[clamp(2rem,6.5vw,3.2rem)] font-black uppercase tracking-wider text-transparent drop-shadow">
          {d("hero.title")}
        </h1>
        <MutedBand>{d("hero.band")}</MutedBand>

        <p className="mb-3">
          <span className="inline-block rounded-full bg-gradient-to-r from-sky-500 to-sky-300 px-4 py-2 text-[clamp(0.95rem,3.4vw,1.05rem)] font-black uppercase text-white shadow-[0_4px_12px_rgba(14,165,233,0.25)]">
            {d("hero.tagline")}
          </span>
        </p>


        <p className="inline-block rounded-xl bg-slate-100 px-4 py-2 text-[clamp(0.95rem,3.6vw,1rem)] font-medium text-slate-700 shadow">
          {d("hero.contact_line")}
        </p>
      </Section>

      {/* PROBLEM */}
      <Section>
        <MutedBand>{d("problem.heading")}</MutedBand>
        <Grid>
          <div>
            <ul className="pl-5 list-disc">
              {bullets("problem.bullets").map((b, i) => (
                <li key={i} className="mb-1">
                  <div className="font-medium">{b.title}</div>
                  {b.note && (
                    <p className="ml-1 mt-1 text-[0.95rem] leading-snug text-slate-500">
                      {b.note}
                    </p>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sky-900 shadow">
              {d("problem.impact")}
            </div>
          </div>

          <div>
            <ZoomImg
              src="/appointments/probleme.png"
              alt={d("images.problem")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* SOLUTION */}
      <Section>
        <MutedBand>{d("solution.heading")}</MutedBand>
        <Grid>
          <div>
            <ul className="pl-5 list-disc">
              {bullets("solution.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>

            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {kpis.map((k, i) => (
                <div
                  key={i}
                  className="min-w-[120px] rounded-xl border border-slate-900/10 bg-white px-2.5 py-2 text-center"
                >
                  <h3 className="m-0 text-[1.05rem] font-extrabold">
                    {k.value}
                  </h3>
                  <p className="m-0 text-sm text-slate-500">{k.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <ImageRow>
              <ZoomImg
                src="/appointments/solutii.png"
                alt={d("images.solution_1")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/alegeserviciu.png"
                alt={d("images.solution_2")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/selectaredata.png"
                alt={d("images.solution_3")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/selectareoredisponibile.png"
                alt={d("images.solution_4")}
                onOpen={openLB}
              />
            </ImageRow>
          </div>
        </Grid>
      </Section>

      {/* PRODUCT TOUR */}
      <Section center>
        <MutedBand>{d("tour.heading")}</MutedBand>
        <p className="inline-block rounded-xl bg-slate-100 px-4 py-2 text-[clamp(0.95rem,3.6vw,1rem)] font-medium text-slate-700 shadow">
          {d("tour.subtitle")}
        </p>
      </Section>

      {/* PUBLIC BOOKING */}
      <Section>
        <MutedBand>{d("public_booking.heading")}</MutedBand>
        <Grid>
          <div>
            <ul className="pl-5 list-disc">
              {bullets("public_booking.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <ZoomImg
              src="/appointments/rezervaripublic.png"
              alt={d("images.public_booking")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* STAFF CALENDAR */}
      <Section>
        <MutedBand>{d("staff_calendar.heading")}</MutedBand>
        <Grid>
          <div>
            <ul className="pl-5 list-disc">
              {bullets("staff_calendar.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <ImageRow>
              <ZoomImg
                src="/appointments/calendarprogramari.png"
                alt={d("images.staff_1")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/detaliiprogramareclient.png"
                alt={d("images.staff_2")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/calendarprogramariadaptiv.png"
                alt={d("images.staff_3")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/detaliiprogramare.png"
                alt={d("images.staff_4")}
                onOpen={openLB}
              />
            </ImageRow>
          </div>
        </Grid>
      </Section>

      {/* MESSAGING */}
      <Section>
        <MutedBand>{d("messaging.heading")}</MutedBand>
        <Grid>
          <div>
            <ul className="pl-5 list-disc">
              {bullets("messaging.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <ImageRow>
              <ZoomImg
                src="/appointments/mesageriesms.png"
                alt={d("images.messaging_1")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/mesageriewhatsapp.png"
                alt={d("images.messaging_2")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/mesajrecenzie.png"
                alt={d("images.messaging_3")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/mesajanularefrizer.png"
                alt={d("images.messaging_4")}
                onOpen={openLB}
              />
            </ImageRow>
          </div>
        </Grid>
      </Section>

      {/* ADD CLIENTS */}
      <Section>
        <MutedBand>{d("add_clients.heading")}</MutedBand>
        <Grid>
          <div>
            <ol className="pl-0 list-none">
              {bullets("add_clients.steps").map((txt, i) => (
                <li key={i} className="relative pl-6 mb-1">
                  <span className="absolute left-0 top-0 font-black text-sky-500">
                    {i + 1}.
                  </span>
                  {txt}
                </li>
              ))}
            </ol>
          </div>
          <div>
            <ZoomImg
              src="/appointments/adaugaclienti.png"
              alt={d("images.add_clients")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* CLIENTS & REVIEWS */}
      <Section>
        <MutedBand>{d("clients_reviews.heading")}</MutedBand>
        <Grid>
          <div>
            <ul className="pl-5 list-disc">
              {bullets("clients_reviews.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="grid grid-cols-2 gap-4">
              <ZoomImg
                src="/appointments/lasarecenzie.png"
                alt={d("images.clients_reviews_1")}
                onOpen={openLB}
              />
              <ZoomImg
                src="/appointments/raspunsrecenzie.png"
                alt={d("images.clients_reviews_2")}
                onOpen={openLB}
              />
            </div>
          </div>
        </Grid>
      </Section>

      {/* SERVICES ADMIN */}
      <Section>
        <MutedBand>{d("services_admin.heading")}</MutedBand>
        <Grid>
          <div>
            <ul className="pl-5 list-disc">
              {bullets("services_admin.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <ZoomImg
              src="/appointments/servicii.png"
              alt={d("images.services_admin")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* ADD BARBER */}
      <Section>
        <MutedBand>{d("add_barber.heading")}</MutedBand>
        <Grid>
          <div>
            <ol className="pl-0 list-none">
              {bullets("add_barber.steps").map((txt, i) => (
                <li key={i} className="relative pl-6 mb-1">
                  <span className="absolute left-0 top-0 font-black text-sky-500">
                    {i + 1}.
                  </span>
                  {txt}
                </li>
              ))}
            </ol>
          </div>
          <div>
            <ZoomImg
              src="/appointments/adaugafrizer.png"
              alt={d("images.add_barber")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* PROFILE PHOTO */}
      <Section>
        <MutedBand>{d("profile_photo.heading")}</MutedBand>
        <Grid>
          <div>
            <ol className="pl-0 list-none">
              {bullets("profile_photo.steps").map((txt, i) => (
                <li key={i} className="relative pl-6 mb-1">
                  <span className="absolute left-0 top-0 font-black text-sky-500">
                    {i + 1}.
                  </span>
                  {txt}
                </li>
              ))}
            </ol>
          </div>
          <div>
            <ZoomImg
              src="/appointments/pozaprofil.png"
              alt={d("images.profile_photo")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* PERSONAL PAGE */}
      <Section>
        <MutedBand>{d("personal_page.heading")}</MutedBand>
        <Grid>
          <div>
            <ul className="pl-5 list-disc">
              {bullets("personal_page.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <ZoomImg
              src="/appointments/rezervaripublic.png"
              alt={d("images.personal_page")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* QR & BUSINESS CARD */}
      <Section>
        <MutedBand>{d("qr_card.heading")}</MutedBand>
        <Grid>
          <div>
            <ol className="pl-0 list-none">
              {bullets("qr_card.steps").map((txt, i) => (
                <li key={i} className="relative pl-6 mb-1">
                  <span className="absolute left-0 top-0 font-black text-sky-500">
                    {i + 1}.
                  </span>
                  {txt}
                </li>
              ))}
            </ol>
            <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sky-900 shadow">
              {d("qr_card.tip")}
            </div>
          </div>
          <div>
            <ZoomImg
              src="/appointments/cartedevizita.png"
              alt={d("images.qr_card")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* WORK SCHEDULE */}
      <Section>
        <MutedBand>{d("work_schedule.heading")}</MutedBand>
        <Grid>
          <div>
            <ol className="pl-0 list-none">
              {bullets("work_schedule.steps").map((txt, i) => (
                <li key={i} className="relative pl-6 mb-1">
                  <span className="absolute left-0 top-0 font-black text-sky-500">
                    {i + 1}.
                  </span>
                  {txt}
                </li>
              ))}
            </ol>
            <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sky-900 shadow">
              {d("work_schedule.tip")}
            </div>
          </div>
          <div>
            <ZoomImg
              src="/appointments/programdelucru.png"
              alt={d("images.work_schedule")}
              onOpen={openLB}
            />
          </div>
        </Grid>
      </Section>

      {/* ARCHITECTURE */}
      <Section>
        <MutedBand>{d("architecture.heading")}</MutedBand>
        <div className="grid items-start gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 text-center">
              <span className="inline-block rounded-full bg-gradient-to-r from-sky-500 to-sky-300 px-4 py-2 text-[clamp(0.95rem,3.4vw,1.05rem)] font-black uppercase text-white shadow-[0_4px_12px_rgba(14,165,233,0.25)]">
                {d("architecture.frontend.title")}
              </span>
            </p>
            <ul className="pl-5 list-disc">
              {bullets("architecture.frontend.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-2 text-center">
              <span className="inline-block rounded-full bg-gradient-to-r from-sky-500 to-sky-300 px-4 py-2 text-[clamp(0.95rem,3.4vw,1.05rem)] font-black uppercase text-white shadow-[0_4px_12px_rgba(14,165,233,0.25)]">
                {d("architecture.backend.title")}
              </span>
            </p>
            <ul className="pl-5 list-disc">
              {bullets("architecture.backend.bullets").map((txt, i) => (
                <li key={i} className="mb-1">
                  {txt}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ROADMAP */}
      <Section>
        <MutedBand>{d("roadmap.heading")}</MutedBand>
        <ul className="pl-5 list-disc">
          {bullets("roadmap.items").map((txt, i) => (
            <li key={i} className="mb-1">
              {txt}
            </li>
          ))}
        </ul>
      </Section>

      {/* Bottom CTA */}
      <div className="mx-auto max-w-6xl px-4 pb-12 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-slate-800">
            <div className="text-lg font-semibold">{d("cta_bottom.title")}</div>
            <div className="text-sm text-slate-600">{d("cta_bottom.subtitle")}</div>
          </div>
          <LocalizedLink
            to="/contact"
            className="rounded-xl bg-indigo-600 px-4 py-2 text-white text-sm font-medium hover:bg-indigo-700"
          >
            {t("applications.cta.contact")}
          </LocalizedLink>
        </div>
      </div>
    </Fragment>
  );
}
